/**
 * Meta Pixel — thin typed wrapper around the global `fbq` injected by
 * the snippet in public/index.html.
 *
 * All functions are no-ops when the pixel hasn't loaded (ad blockers,
 * dev with no network, etc.) — never throws, never blocks the user
 * flow on tracking failure.
 *
 * Pixel ID: 1145802097714641
 *
 * Event taxonomy:
 *   PageView            — fired automatically by the snippet + by trackPageView()
 *                         on SPA route changes
 *   Lead                — user submitted the register form (account created)
 *   CompleteRegistration — user finished the 4-step onboarding wizard
 *   Subscribe           — user upgraded to Pro (recurring purchase)
 *
 * These match Meta's standard event catalogue so ad-set optimisation
 * works without custom-event mapping.
 */

declare global {
  interface Window {
    fbq?: (
      command: 'track' | 'trackCustom' | 'init',
      eventName: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

const isAvailable = (): boolean => typeof window !== 'undefined' && typeof window.fbq === 'function';

/**
 * Fire a PageView event. Call this on SPA route changes — the snippet
 * only fires it once on initial HTML load, so client-side navigations
 * would otherwise be invisible to Meta.
 */
export function trackPageView(): void {
  if (!isAvailable()) return;
  try { window.fbq!('track', 'PageView'); } catch { /* ignore */ }
}

/** Fire when a user completes the register form (account exists in DB). */
export function trackLead(extras?: { email?: string; method?: string }): void {
  if (!isAvailable()) return;
  try {
    window.fbq!('track', 'Lead', extras as Record<string, unknown>);
  } catch { /* ignore */ }
}

/** Fire when a user finishes the onboarding wizard. */
export function trackCompleteRegistration(extras?: { method?: string }): void {
  if (!isAvailable()) return;
  try {
    window.fbq!('track', 'CompleteRegistration', extras as Record<string, unknown>);
  } catch { /* ignore */ }
}

/**
 * Fire when a user upgrades to Pro. value+currency let Meta optimise
 * toward higher-ARPU prospects on Advantage+ campaigns.
 */
export function trackSubscribe(params: { value: number; currency?: string; plan?: string }): void {
  if (!isAvailable()) return;
  try {
    window.fbq!('track', 'Subscribe', {
      value: params.value,
      currency: params.currency || 'USD',
      predicted_ltv: params.value * 12, // rough annual LTV signal
      ...(params.plan ? { plan: params.plan } : {}),
    });
  } catch { /* ignore */ }
}
